import {
  Lock,
  Mail,
  Trash2,
  HeartHandshake,
  BookOpen,
  ArrowLeft,
  Check,
  Library,
  Lightbulb,
  PenLine,
  Shield,
  SlidersHorizontal,
  Undo2,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, IconChip } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { StatusGroup } from "@/components/status/status-button";
import { StreakCard } from "@/components/recovery/streak-card";
import { SobrietyCard } from "@/components/recovery/sobriety-card";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/logo";
import { PublicFooter, PublicTopBar } from "@/components/public/chrome";
import {
  Band,
  BitList,
  BODY,
  CtaBlock,
  EYEBROW,
  Glimpse,
  INNER,
  SectionHeading,
} from "@/components/public/section";
import { STATUS_SHAPES, type StatusKey } from "@/lib/status";
import { useState } from "react";

// Happy24 Landing Page B — the public marketing page. Ported from the
// "Happy24" Claude Design project (screens/landing.jsx, LandingB / LandingPage
// composition). Full-bleed and responsive: a single column on phones, two
// columns from 900px up. Copy describes only what ships today.
//
// The chrome (top bar, footer, Band, Glimpse, CtaBlock, BitList, the layout
// constants) now lives in src/components/public/ so the Literature page — and
// the legal pages after it — share one copy of it.

function PhoneMock() {
  const [value, setValue] = useState<StatusKey | undefined>();
  return (
    <div className="flex flex-col gap-2.5">
      <div className="overflow-hidden rounded-[20px] border border-border bg-card shadow-md">
        <div className="flex items-center gap-3 px-[18px] pb-1.5 pt-[18px]">
          <ArrowLeft className="h-[18px] w-[18px] text-muted-foreground" strokeWidth={2} />
          <div className="flex-1">
            <Progress value={35} />
          </div>
        </div>
        <div className="px-[18px] pb-5 pt-3.5">
          <p className={EYEBROW}>Question 2 of 7</p>
          <h3 className="mb-[18px] mt-1.5 text-lg font-semibold text-foreground">
            Was I resentful today?
          </h3>
          <StatusGroup
            value={value}
            onChange={setValue}
            labels={{
              green: "No — I let things go",
              yellow: "A little lingered",
              red: "Yes — it's eating at me",
            }}
          />
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground">
        From tonight's check-in — try it. Every answer is honest, none is wrong.
      </p>
    </div>
  );
}

const STEPS = [
  {
    n: "1",
    t: "A quiet prompt each evening",
    d: "Whenever your day winds down — no alarms, no nagging.",
  },
  {
    n: "2",
    t: "A few honest questions",
    d: "Answer Green, Yellow, or Red. Add a note if you want. It takes a few minutes — under one, if you're tired.",
  },
  {
    n: "3",
    t: "Your day, kept privately",
    d: "Each check-in joins your history, so you can notice patterns over weeks — not judge single days.",
  },
];

function Steps() {
  return (
    <div className="flex flex-col gap-[18px]">
      {STEPS.map((s) => (
        <div key={s.n} className="flex items-start gap-3.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[13px] font-semibold text-primary">
            {s.n}
          </div>
          <div>
            <p className="text-[15px] font-semibold text-foreground">{s.t}</p>
            <p className="mt-0.5 text-sm leading-[1.55] text-muted-foreground">
              {s.d}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

const TRUST: { icon: LucideIcon; t: string; d: string }[] = [
  {
    icon: Lock,
    t: "Private by default",
    d: "Nothing is shared unless you choose to share it. Your entries are yours alone.",
  },
  {
    icon: Mail,
    t: "Only an email",
    d: "That's all we collect. No name, no phone number, no profile.",
  },
  {
    icon: Trash2,
    t: "Leave completely",
    d: "Delete your account any time. It's immediate, and it removes everything.",
  },
];

function TrustStrip() {
  return (
    <div className="flex flex-col gap-3.5 min-[900px]:grid min-[900px]:grid-cols-3 min-[900px]:gap-8">
      {TRUST.map(({ icon: Icon, t, d }) => (
        <div key={t} className="flex items-start gap-3">
          <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
          <p className="text-sm leading-[1.55] text-foreground">
            <strong className="font-semibold">{t}.</strong>{" "}
            <span className="text-muted-foreground">{d}</span>
          </p>
        </div>
      ))}
    </div>
  );
}

function CompleteGlimpse() {
  return (
    <Glimpse caption="The end of a check-in. No confetti — just done.">
      <div className="flex flex-col items-center gap-2.5 py-2.5 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Check className="h-6 w-6" strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-[17px] font-bold text-foreground">
            Check-In Complete
          </p>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            You're aware for today.
          </p>
        </div>
        <Badge status="green">
          <StatusShape s="green" /> Today: Green
        </Badge>
      </div>
    </Glimpse>
  );
}

function HomeGlimpse() {
  return (
    <Glimpse caption="Your home screen — quiet continuity, no scoreboard. The word follows the literature you use: sober, clean, or abstinent.">
      <div className="flex flex-col gap-3">
        <StreakCard days={12} bestStreak={30} checkedInToday />
        {/* Deliberately "Clean Time" rather than the default: the caption
            claims the word follows your literature, so the mock should show
            a word other than AA's. */}
        <SobrietyCard startDate="2026-06-04" label="Clean Time" onReset={() => {}} />
      </div>
    </Glimpse>
  );
}

const SHORT: Record<StatusKey, string> = {
  green: "On Track",
  yellow: "Caution",
  red: "Needs Attention",
};

function StatusShape({ s }: { s: StatusKey }) {
  const Shape = STATUS_SHAPES[s];
  return <Shape className="h-[11px] w-[11px]" strokeWidth={2} />;
}

const HISTORY: { when: string; meta: string; s: StatusKey }[] = [
  { when: "Today", meta: "8:41 PM · 7 questions", s: "green" },
  { when: "Yesterday", meta: "9:15 PM · Quick · 2 questions", s: "green" },
  { when: "Jul 17", meta: "11:30 PM · 7 questions", s: "red" },
];

function HistoryGlimpse() {
  return (
    <Glimpse caption="Your history — every day counts, including the hard ones.">
      <p className={`${EYEBROW} mb-1.5`}>Recent entries</p>
      <div className="flex flex-col">
        {HISTORY.map((e, i) => (
          <div
            key={e.when}
            className={`flex items-center justify-between gap-3 py-2.5 ${
              i < HISTORY.length - 1 ? "border-b border-border" : ""
            }`}
          >
            <div>
              <p className="text-sm font-medium text-foreground">{e.when}</p>
              <p className="mt-px text-xs text-muted-foreground">{e.meta}</p>
            </div>
            <Badge status={e.s}>
              <StatusShape s={e.s} /> {SHORT[e.s]}
            </Badge>
          </div>
        ))}
      </div>
    </Glimpse>
  );
}

/* ------------------------------ step work ------------------------------ */

// Steps 1–5 ship today, with the principles they were seeded with. The
// landing page only ever describes what you can actually use.
const STEP_ROWS: [number, string, "done" | "current" | "ahead"][] = [
  [1, "Honesty", "done"],
  [2, "Hope", "done"],
  [3, "Willingness", "current"],
  [4, "Courage", "ahead"],
  [5, "Integrity", "ahead"],
];

function StepsGlimpse() {
  return (
    <Glimpse caption="Steps 1 through 5, worked at your own pace.">
      <p className={`${EYEBROW} mb-2`}>Your steps</p>
      <div className="flex flex-col gap-0.5">
        {STEP_ROWS.map(([n, name, state]) => (
          <div
            key={n}
            className={`flex items-center gap-3 py-[7px] ${state === "ahead" ? "opacity-50" : ""}`}
          >
            <span
              className={`inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                state === "done"
                  ? "bg-primary text-primary-foreground"
                  : state === "current"
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
              }`}
            >
              {state === "done" ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : n}
            </span>
            <span
              className={`flex-1 text-sm ${
                state === "current" ? "font-semibold text-foreground" : ""
              } ${state === "ahead" ? "text-muted-foreground" : "text-foreground"}`}
            >
              Step {n} — {name}
            </span>
            {state === "current" && (
              <span className="text-xs text-muted-foreground">In progress</span>
            )}
          </div>
        ))}
      </div>
      <p className="mt-2.5 flex items-center gap-1.5 border-t border-border pt-2.5 text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5" strokeWidth={2} /> Private — only you can see this
      </p>
    </Glimpse>
  );
}

const STEP_WORK: { icon: LucideIcon; t: string; d: string }[] = [
  {
    icon: BookOpen,
    t: "Reading, then your own words",
    d: "Each step points to the reading in your own book, then asks a few questions. Nothing is timed, and you can leave and come back.",
  },
  {
    icon: Library,
    t: "Your literature, or none",
    d: "Tell the app which literature you use and the readings cite it: AA, NA, CA, GA, or OA, and more than one if you're in more than one. Say nothing and they simply point at your own book.",
  },
  {
    icon: Lock,
    t: "No AI anywhere in step work",
    d: "Nothing interprets your answers or suggests what to believe. Steps 2 and 3 stay open — a power greater than yourself, however you understand that.",
  },
  {
    icon: Shield,
    t: "Step 4 is yours alone",
    d: "The inventory is encrypted on your own device — we can't read it, and there's no way to share it. Step 5 is you reading it aloud, in person, and then it's yours to delete.",
  },
];

/* ------------------------------- sponsor ------------------------------- */

const SPONSOR_ROWS: { n: string; m: string; s: StatusKey }[] = [
  { n: "Priya N.", m: "21 days · Today · 10:14 PM", s: "red" },
  { n: "Jordan R.", m: "96 days · Today · 7:30 PM", s: "yellow" },
  { n: "Marcus D.", m: "2 yr 4 mo · Today · 9:02 PM", s: "green" },
];

function SponsorGlimpse() {
  return (
    <Glimpse caption="What a sponsor sees — only what was shared with them.">
      <p className={`${EYEBROW} mb-2`}>Sponsees</p>
      <div className="flex flex-col">
        {SPONSOR_ROWS.map((r, i) => (
          <div
            key={r.n}
            className={`flex items-center gap-3 py-2.5 ${
              i < SPONSOR_ROWS.length - 1 ? "border-b border-border" : ""
            }`}
          >
            <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
              {r.n[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{r.n}</p>
              <p className="mt-px text-xs text-muted-foreground">{r.m}</p>
            </div>
            <Badge status={r.s}>
              <StatusShape s={r.s} /> {SHORT[r.s]}
            </Badge>
          </div>
        ))}
      </div>
    </Glimpse>
  );
}

const SPONSOR_BITS: { icon: LucideIcon; t: string; d: string }[] = [
  {
    icon: HeartHandshake,
    t: "You invite them",
    d: "A sponsor can only see your entries after you ask them and they accept. Nothing is shared before that.",
  },
  {
    icon: SlidersHorizontal,
    t: "You choose what they see",
    d: "Share your daily check-ins, a step's reflections, or nothing at all — just a quiet note that you're ready to talk.",
  },
  {
    icon: Undo2,
    t: "You can take it back",
    d: "Stop sharing any time, and they stop seeing it. Sponsors read; they never edit or score your work.",
  },
];

/* -------------------------------- extras -------------------------------- */

const EXTRAS: { icon: LucideIcon; t: string; d: string }[] = [
  {
    icon: Lightbulb,
    t: "Bright Ideas",
    d: "Catch the plan that arrives at 11 PM. Keep it, sit with it, share it with your sponsor if you want to.",
  },
  {
    icon: PenLine,
    t: "A short reflection",
    d: "After a check-in you get a few sentences back — drawn from what you wrote, and nothing else. It may point at a reading from the literature you chose, and cites nothing if you chose none. It never tells you what to believe.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-dvh bg-background font-sans">
      <PublicTopBar />

      {/* Hero B */}
      <Band>
        <div
          className={`${INNER} py-12 min-[900px]:grid min-[900px]:grid-cols-[minmax(0,1.1fr)_400px] min-[900px]:items-center min-[900px]:gap-16 min-[900px]:py-20`}
        >
          <div>
            <h1 className="mb-3.5 text-3xl font-bold leading-[1.2] text-foreground [text-wrap:pretty] min-[900px]:text-[40px]">
              A quiet daily check-in for your recovery.
            </h1>
            <p className={`${BODY} mb-6 text-base`}>
              A few honest questions at the end of each day. Private, plain, and
              yours.
            </p>
            <CtaBlock />
          </div>
          <div className="mt-6 min-[900px]:mt-0">
            <PhoneMock />
          </div>
        </div>
      </Band>

      {/* Trust */}
      <Band secondary>
        <div className={`${INNER} py-10 min-[900px]:py-16`}>
          <SectionHeading eyebrow="Trust" title="Private first — always." />
          <TrustStrip />
        </div>
      </Band>

      {/* How it works */}
      <Band>
        <div className={`${INNER} py-10 min-[900px]:py-16`}>
          <SectionHeading
            eyebrow="How it works"
            title="Three quiet steps, most evenings."
          />
          <div className="flex flex-col gap-6 min-[900px]:grid min-[900px]:grid-cols-2 min-[900px]:items-start min-[900px]:gap-12">
            <Steps />
            <CompleteGlimpse />
          </div>
          <p className="mt-5 text-[13px] leading-[1.55] text-muted-foreground">
            The daily inventory follows the tradition of Step 10. Happy24 is
            independent — not affiliated with or endorsed by any twelve-step
            fellowship.
          </p>
        </div>
      </Band>

      {/* Over time */}
      <Band secondary>
        <div className={`${INNER} py-10 min-[900px]:py-16`}>
          <SectionHeading eyebrow="Over time" title="Patterns, not report cards." />
          <p className={`${BODY} mb-5`}>
            Each check-in joins your history. Streaks read as gentle continuity,
            and however you count your time — sober, clean, or abstinent — it's
            held with quiet dignity. Nothing is scored, nothing is lost on a hard
            day.
          </p>
          <div className="flex flex-col gap-6 min-[900px]:grid min-[900px]:grid-cols-2 min-[900px]:items-start min-[900px]:gap-12">
            <HomeGlimpse />
            <HistoryGlimpse />
          </div>
        </div>
      </Band>

      {/* Step work */}
      <Band>
        <div className={`${INNER} py-10 min-[900px]:py-16`}>
          <SectionHeading
            eyebrow="Step work"
            title="Steps 1 through 5, at your own pace."
          />
          <div className="flex flex-col gap-6 min-[900px]:grid min-[900px]:grid-cols-2 min-[900px]:items-start min-[900px]:gap-12">
            <BitList bits={STEP_WORK} />
            <StepsGlimpse />
          </div>
          <p className="mt-5 text-[13px] leading-[1.55] text-muted-foreground">
            Readings can be cited from Alcoholics Anonymous, Narcotics Anonymous,
            Cocaine Anonymous, Gamblers Anonymous, and Overeaters Anonymous, or from
            no fellowship at all, if you work the steps on your own, with a therapist,
            or from a book of your own. Happy24 never reproduces any fellowship's
            literature. A reading is a title and a page range you read in your own
            copy.{" "}
            <Link to="/literature" className="text-primary underline">
              See how that works →
            </Link>
          </p>
        </div>
      </Band>

      {/* With a sponsor */}
      <Band secondary>
        <div className={`${INNER} py-10 min-[900px]:py-16`}>
          <SectionHeading eyebrow="With a sponsor" title="Shared only when you say so." />
          <div className="flex flex-col gap-6 min-[900px]:grid min-[900px]:grid-cols-2 min-[900px]:items-start min-[900px]:gap-12">
            <BitList bits={SPONSOR_BITS} />
            <SponsorGlimpse />
          </div>
        </div>
      </Band>

      {/* Differentiator */}
      <Band>
        <div className={`${INNER} py-10 min-[900px]:py-16`}>
          <SectionHeading
            eyebrow="Honest reflection"
            title="You add to your history. You never rewrite it."
          />
          <div className="flex flex-col gap-4 min-[900px]:grid min-[900px]:grid-cols-2 min-[900px]:items-start min-[900px]:gap-12">
            <p className={BODY}>
              Entries can't be edited after you save them. If tomorrow-you sees
              yesterday differently, you add a revision alongside the original —
              the way an honest inventory works on paper. What you wrote stays
              what you wrote.
            </p>
            <Card>
              <CardContent className="p-4">
                <p className={EYEBROW}>Jul 17 · 11:30 PM</p>
                <p className="mt-1.5 text-sm leading-[1.55] text-foreground">
                  Hard day. I was short with everyone and I know why.
                </p>
                <div className="mt-3 border-t border-border pt-3">
                  <p className={EYEBROW}>Revision · Jul 18</p>
                  <p className="mt-1.5 text-sm leading-[1.55] text-muted-foreground">
                    Called my sponsor this morning. It wasn't about them at all.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Band>

      {/* Also in the app */}
      <Band>
        <div className={`${INNER} py-10 min-[900px]:py-16`}>
          <SectionHeading eyebrow="Also in the app" title="Two small things that help." />
          <div className="flex flex-col gap-3 min-[900px]:grid min-[900px]:grid-cols-2 min-[900px]:gap-12">
            {EXTRAS.map(({ icon, t, d }) => (
              <Card key={t}>
                <CardContent className="flex items-start gap-3.5 p-4">
                  <IconChip icon={icon} tint="primary" />
                  <div className="flex-1">
                    <p className="text-[15px] font-semibold text-foreground">{t}</p>
                    <p className="mt-0.5 text-sm leading-[1.5] text-muted-foreground">
                      {d}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </Band>

      {/* Philosophy */}
      <Band secondary>
        <div className="px-6 py-14 text-center">
          <div className="mb-4 flex justify-center">
            <LogoMark size={40} />
          </div>
          <h2 className="text-2xl font-semibold text-foreground">
            One day at a time.
          </h2>
          <p className={`${BODY} mx-auto mt-2.5 max-w-[300px]`}>
            Happy24 doesn't ask you to be perfect. It asks you to notice — for
            the next 24 hours.
          </p>
        </div>
      </Band>

      {/* Final CTA */}
      <Band>
        <div className={`${INNER} py-10 pb-12 min-[900px]:py-16`}>
          <div className="mx-auto min-[900px]:max-w-[560px]">
            <SectionHeading title="Tonight is a fine time to start." />
            <div className="flex flex-col gap-4">
              <p className={`${BODY} text-sm`}>
                Happy24 is free while we launch. If that ever changes, we'll say
                so plainly and ahead of time — and your history stays yours to
                keep or delete either way.
              </p>
              <CtaBlock />
            </div>
          </div>
        </div>
      </Band>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
